import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import API_BASE_URL from '../config';

const EditCustomer = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [customer, setCustomer] = useState(null);

    // Fetch existing customer data on load
    useEffect(() => {
        const fetchCustomerDetails = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/get-customer/${id}`);
                if (response.ok) {
                    const data = await response.json();
                    setCustomer(data);
                } else {
                    alert("Customer not found");
                    navigate('/customershow');
                }
            } catch (error) {
                console.error("Error fetching customer:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchCustomerDetails();
    }, [id, navigate]);

    const handleChange = (e, section, subSection, field) => {
        const { name, value } = e.target;

        if (section === "measurements") {
            const numValue = value === "" ? 0 : parseFloat(value);
            setCustomer(prev => ({
                ...prev,
                measurements: {
                    ...prev.measurements,
                    [subSection]: {
                        ...prev.measurements[subSection],
                        [field]: numValue
                    }
                }
            }));
        } else if (section === "address") {
            setCustomer(prev => ({
                ...prev,
                address: { ...prev.address, [field]: value }
            }));
        } else {
            setCustomer(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const response = await fetch(`${API_BASE_URL}/update-customer/${id}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(customer)
            });
            
            if (response.ok) {
                alert("Profile Updated Successfully!");
                navigate('/customers'); 
            }
        } catch (error) {
            console.error("Update failed:", error);
        }
    };

    const copyToWhatsApp = () => {
        const text = `*Customer:* ${customer.custName}%0A*Shirt:* L:${customer.measurements.shirt.length} C:${customer.measurements.shirt.chest} W:${customer.measurements.shirt.waist} H:${customer.measurements.shirt.hip}%0A*Salwar:* L:${customer.measurements.salwar.salwarLength} M:${customer.measurements.salwar.mohri}`;
        window.open(`https://wa.me/${customer.custPhone}?text=${text}`, '_blank');
    };

    if (loading) return <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>;
    if (!customer) return null;

    return (
        <div className="container-fluid py-5 bg-light min-vh-100">
            <style>
                {`
                    .form-card { border: none; border-radius: 15px; box-shadow: 0 10px 30px rgba(0,0,0,0.05); background: #fff; }
                    .section-title { font-size: 0.9rem; font-weight: 800; color: #0d6efd; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 20px; border-bottom: 2px solid #eef2f7; padding-bottom: 10px; }
                    .form-label { font-weight: 600; color: #495057; font-size: 0.85rem; }
                    .form-control { border: 1px solid #e0e6ed; padding: 0.6rem 1rem; border-radius: 8px; }
                    .measurement-input { background-color: #f8f9fa; border-color: #dee2e6; font-weight: bold; color: #0d6efd; }
                `}
            </style>

            <div className="container">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h2 className="fw-bold mb-0 text-dark">Edit <span className="text-primary">Profile</span></h2>
                        <code className="text-muted">ID: {id}</code>
                    </div>
                    <div className="d-flex gap-2">
                        <button onClick={copyToWhatsApp} className="btn btn-success fw-bold px-3 rounded-pill shadow-sm">
                            <i className="bi bi-whatsapp me-2"></i>Share
                        </button>
                        <button onClick={() => navigate(-1)} className="btn btn-outline-secondary fw-bold px-4 rounded-pill">Back</button>
                    </div>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="row g-4">
                        <div className="col-lg-8">
                            <div className="card form-card p-4 mb-4">
                                <h6 className="section-title">Personal Details</h6>
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label">Full Name *</label>
                                        <input type="text" name="custName" required className="form-control" value={customer.custName} onChange={handleChange} />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Phone Number</label>
                                        <input type="text" name="custPhone" className="form-control" value={customer.custPhone} onChange={handleChange} />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Email Address</label>
                                        <input type="email" name="custEmail" className="form-control" value={customer.custEmail} onChange={handleChange} />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Instagram ID</label>
                                        <input type="text" name="custInsta" className="form-control" value={customer.custInsta} onChange={handleChange} />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">Gender</label>
                                        <select name="gender" className="form-select" value={customer.gender} onChange={handleChange}>
                                            <option value="Female">Female</option>
                                            <option value="Male">Male</option>
                                            <option value="Other">Other</option>
                                            <option value="Unspecified">Unspecified</option>
                                        </select>
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">Height</label>
                                        <input type="text" name="custHeight" className="form-control" value={customer.custHeight} onChange={handleChange} />
                                    </div>
                                </div>
                            </div>

                            <div className="card form-card p-4">
                                <h6 className="section-title">Address & Location</h6>
                                <div className="row g-3">
                                    <div className="col-md-8">
                                        <label className="form-label">Street</label>
                                        <input type="text" className="form-control" value={customer.address.street} onChange={(e) => handleChange(e, "address", null, "street")} />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">Country</label>
                                        <input type="text" className="form-control" value={customer.address.country} onChange={(e) => handleChange(e, "address", null, "country")} />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">City</label>
                                        <input type="text" className="form-control" value={customer.address.city} onChange={(e) => handleChange(e, "address", null, "city")} />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">State</label>
                                        <input type="text" className="form-control" value={customer.address.state} onChange={(e) => handleChange(e, "address", null, "state")} />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">Pincode</label>
                                        <input type="text" className="form-control" value={customer.address.pincode} onChange={(e) => handleChange(e, "address", null, "pincode")} />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-lg-4">
                            <div className="card form-card p-4 mb-4 border-top border-primary border-4">
                                <h6 className="text-primary fw-bold mb-3">SHIRT (Inches)</h6>
                                <div className="row g-2">
                                    {['length', 'chest', 'waist', 'hip', 'sleeveLength', 'mohri'].map((field) => (
                                        <div className="col-6" key={field}>
                                            <label className="small fw-bold text-muted text-capitalize">{field}</label>
                                            <input type="number" step="0.1" className="form-control form-control-sm measurement-input" value={customer.measurements.shirt[field]} onChange={(e) => handleChange(e, "measurements", "shirt", field)} />
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="card form-card p-4 border-top border-success border-4">
                                <h6 className="text-success fw-bold mb-3">SALWAR (Inches)</h6>
                                <div className="row g-2">
                                    <div className="col-6">
                                        <label className="small fw-bold text-muted">Length</label>
                                        <input type="number" step="0.1" className="form-control form-control-sm measurement-input" value={customer.measurements.salwar.salwarLength} onChange={(e) => handleChange(e, "measurements", "salwar", "salwarLength")} />
                                    </div>
                                    <div className="col-6">
                                        <label className="small fw-bold text-muted">Mohri</label>
                                        <input type="number" step="0.1" className="form-control form-control-sm measurement-input" value={customer.measurements.salwar.mohri} onChange={(e) => handleChange(e, "measurements", "salwar", "mohri")} />
                                    </div>
                                </div>
                            </div>

                            <button type="submit" className="btn btn-primary w-100 py-3 mt-4 fw-bold shadow">
                                UPDATE CHANGES
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditCustomer;
